import React from 'react';
import {
  FormTemplateQuestionFieldOptionsValidityHintEnum,
  TaskCompletion,
  TaskCompletionStatusEnum,
} from 'graphql-common';
import { TFunction } from 'i18next';
import { Column, CellProps } from '@lib/components/Table/types';
import TextLinkCell from '@lib/components/Table/commonComponents/TextLinkCell/TextLinkCell';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { dateFormat, dateTimeFormatEU } from '@lib/enums/dateTime';
import UserPreviewCell from '@lib/components/Table/commonComponents/UserPreviewCell/UserPreviewCell';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import ChipWithDropDown from '@lib/components/ChipWithDropDown/ChipWithDropDown';
import Menu, { NavItemTypeEnum } from '@lib/components/Menu/Menu';
import getResultStateChipType from 'utils/getResultStateChipType';
import StatusBox from '@lib/components/StatusBox/StatusBox';
import ValidityHint from '@lib/enums/validityHint';
import getCompletionDocumentsReadStatus from './getCompletionDocumentsReadStatus';
import styles from './TaskReportingTableColumns.module.scss';

export type StatusChangeHandler = (
  completion: TaskCompletion,
  status: TaskCompletionStatusEnum,
) => void;

const statusOptions = [
  TaskCompletionStatusEnum.Unsolved,
  TaskCompletionStatusEnum.InProgress,
  TaskCompletionStatusEnum.Solved,
];

function StatusOptionChip({ status }: { status: TaskCompletionStatusEnum }) {
  return (
    <Chip
      leftIcon="circle"
      type={getResultStateChipType(status)}
      size={ChipSizes.smallLeftIconOnly}
    />
  );
}

const statusOptionIcons: Record<
  TaskCompletionStatusEnum,
  () => React.ReactNode
> = {
  [TaskCompletionStatusEnum.Unsolved]: () => (
    <StatusOptionChip status={TaskCompletionStatusEnum.Unsolved} />
  ),
  [TaskCompletionStatusEnum.InProgress]: () => (
    <StatusOptionChip status={TaskCompletionStatusEnum.InProgress} />
  ),
  [TaskCompletionStatusEnum.Solved]: () => (
    <StatusOptionChip status={TaskCompletionStatusEnum.Solved} />
  ),
};

function getStatusCounts(answers: TaskCompletion['answers']) {
  return (answers || []).reduce(
    (acc, answer) => {
      const { validityHint } = answer || {};
      if (
        validityHint ===
        FormTemplateQuestionFieldOptionsValidityHintEnum.Failure
      ) {
        acc.failure += 1;
      } else if (
        validityHint ===
        FormTemplateQuestionFieldOptionsValidityHintEnum.Warning
      ) {
        acc.warning += 1;
      } else if (
        validityHint ===
        FormTemplateQuestionFieldOptionsValidityHintEnum.Success
      ) {
        acc.success += 1;
      } else {
        acc.none += 1;
      }
      return acc;
    },
    { none: 0, success: 0, warning: 0, failure: 0 },
  );
}

function StatusBoxes({ completion }: { completion: TaskCompletion }) {
  const counts = getStatusCounts(completion.answers);
  const boxes = [
    { type: ValidityHint.Failure, value: counts.failure },
    { type: ValidityHint.Warning, value: counts.warning },
    { type: ValidityHint.Success, value: counts.success },
  ].filter(({ value }) => value > 0);

  if (!boxes.length) {
    return <StatusBox text={0} type={ValidityHint.None} />;
  }

  return (
    <div className={styles.statusBoxes}>
      {boxes.map(({ type, value }) => (
        <StatusBox key={type} text={value} type={type} />
      ))}
    </div>
  );
}

function getStatusLabel(
  status: TaskCompletionStatusEnum | null | undefined,
  t: TFunction<'translation', undefined>,
) {
  if (!status) return undefined;
  switch (status) {
    case TaskCompletionStatusEnum.InProgress:
      return t('in-progress');
    case TaskCompletionStatusEnum.Solved:
      return t('solved');
    case TaskCompletionStatusEnum.Unsolved:
      return t('unsolved');
    default:
      return undefined;
  }
}

function CompletionStatusCell({
  completion,
  t,
  isStatusUpdating,
  onStatusChange,
}: {
  completion: TaskCompletion;
  t: TFunction<'translation', undefined>;
  isStatusUpdating?: boolean;
  onStatusChange?: StatusChangeHandler;
}) {
  const { status, canStatusUpdate } = completion || {};
  const label = getStatusLabel(status, t);
  if (!label || !status) {
    return <span>-</span>;
  }
  const type = getResultStateChipType(status);
  const canUpdate = canStatusUpdate?.value && onStatusChange;

  if (!canUpdate || isStatusUpdating) {
    return <Chip type={type} label={label} leftIcon="circle" />;
  }

  const getBody = () => (
    <Menu
      navItems={statusOptions
        .filter((item) => item !== status)
        .map((item) => ({
          id: item,
          title: getStatusLabel(item, t),
          type: NavItemTypeEnum.Secondary,
          leftIcon: statusOptionIcons[item],
          onClick: () => onStatusChange(completion, item),
        }))}
    />
  );

  return (
    <ChipWithDropDown
      label={label}
      type={type}
      leftIcon="circle"
      width={180}
      body={getBody}
    />
  );
}

export default function getTaskReportingTableColumns({
  t,
  onViewTaskCompletionHandler,
  onStatusChange,
  isStatusUpdating,
}: {
  t: TFunction<'translation', undefined>;
  onStatusChange?: StatusChangeHandler;
  isStatusUpdating?: boolean;
  onViewTaskCompletionHandler: (item: TaskCompletion) => void;
}): Column<TaskCompletion>[] {
  return [
    {
      id: 'createdAt',
      Header: t('check-date'),
      accessorFn: ({ item }: CellProps<TaskCompletion>) =>
        item.createdAt
          ? getFormattedDate(item.createdAt, dateTimeFormatEU)
          : '-',
      Cell: ({ item, ...restProps }: CellProps<TaskCompletion>) => (
        <TextLinkCell
          {...restProps}
          item={item}
          onClick={() => onViewTaskCompletionHandler(item)}
        />
      ),
      sortable: true,
      minWidth: 160,
    },
    {
      id: 'templateVersionVersion',
      Header: t('version'),
      accessor: 'version',
      Cell: (props: CellProps<TaskCompletion>) => {
        const { item } = props;
        const v = item.task?.formTemplateVersion.version;
        const value = v ? `v${v}` : '-';
        return value;
      },
      minWidth: 100,
    },
    {
      id: 'responsesTotal',
      Header: t('responses-total'),
      accessorFn: ({ item }: CellProps<TaskCompletion>) =>
        (item.answers || []).length,
      Cell: (props: CellProps<TaskCompletion>) => <TextCell {...props} />,
      minWidth: 120,
    },
    {
      id: 'checkStatus',
      Header: t('check-status'),
      accessor: 'answers',
      Cell: ({ item }: CellProps<TaskCompletion>) => (
        <StatusBoxes completion={item} />
      ),
      minWidth: 240,
      headerTooltipInfo: {
        icon: 'info',
        tooltipBody: t('check-status-desc'),
        symbolsOutlined: true,
      },
    },
    {
      id: 'status',
      Header: t('resolution-status'),
      accessor: 'status',
      Cell: ({ item }: CellProps<TaskCompletion>) => (
        <CompletionStatusCell
          completion={item}
          t={t}
          onStatusChange={onStatusChange}
          isStatusUpdating={isStatusUpdating}
        />
      ),
      sortable: true,
      minWidth: 200,
    },
    {
      id: 'solvedAt',
      Header: t('date-solved'),
      accessorFn: ({ item }: CellProps<TaskCompletion>) =>
        item.solvedAt ? getFormattedDate(item.solvedAt, dateFormat) : '-',
      Cell: (props: CellProps<TaskCompletion>) => <TextCell {...props} />,
      sortable: true,
      minWidth: 140,
    },
    {
      id: 'documents',
      Header: t('documents'),
      accessor: 'answers',
      Cell: ({ item }: CellProps<TaskCompletion>) => {
        const status = getCompletionDocumentsReadStatus(item.answers);
        if (status.kind === 'none') {
          return <TextCell value="-" item={item} />;
        }
        if (status.kind === 'all-read') {
          return (
            <Chip
              type={ChipTypes.chipGreen}
              label={t('all-read')}
              size={ChipSizes.small}
            />
          );
        }
        return (
          <Chip
            type={ChipTypes.chipRed}
            label={t('n-unread', { n: status.unreadCount })}
            size={ChipSizes.small}
          />
        );
      },
      minWidth: 140,
    },
    {
      id: 'performerFullName',
      Header: t('performer'),
      accessor: 'performer',
      Cell: ({ item }: CellProps<TaskCompletion>) => {
        const firstName = item?.performer?.firstName || '';
        const lastName = item?.performer?.lastName || '';
        const name = [firstName, lastName].filter(Boolean).join(' ').trim();
        const avatar = item?.performer?.avatarAttached?.urls?.small;
        return name ? (
          <UserPreviewCell name={name} avatar={avatar} />
        ) : (
          <TextCell value="-" item={item} />
        );
      },
      minWidth: 220,
    },
  ];
}
